import {
  type Canvas,
  Circle,
  type FabricObject,
  Line,
  Point,
  Polyline,
  type TPointerEvent,
  util,
} from "fabric";
import {
  type LineLabelDefs,
  type LineShapeGroup,
  type ShapeGroup,
  type ZoneShapeGroup,
  CORNER_COLOR,
  CORNER_STROKE_COLOR,
  LINE_STROKE,
  LINE_STROKE_WIDTH,
  ZONE_FILL,
  ZONE_LABEL_STYLE,
  ZONE_STROKE,
  setShapeGroup,
  getShapeGroup,
} from "./constants";
import {
  clampToImageBounds,
  createLabelGroup,
  getBgImage,
  getDynamicLineLabelStyle,
  getImageNaturalSize,
  getNormalizedPoint,
  pixelToPercentStatic,
} from "./canvas-helpers";
import { getLineGroupEndpoints, rebuildLineGroup } from "./shape-helpers";

export function enterLineCoordinateMode(
  canvas: Canvas,
  sg: LineShapeGroup,
  shapeObjectsRef: React.MutableRefObject<FabricObject[]>,
  activeCoordinateSgRef: React.MutableRefObject<ShapeGroup | null>,
  _lineLabelDefs?: LineLabelDefs,
) {
  const oldGroup = sg.fabricGroup;
  canvas.discardActiveObject();

  const worldPts = getLineGroupEndpoints(oldGroup);

  canvas.remove(oldGroup);
  const idx = shapeObjectsRef.current.indexOf(oldGroup);
  if (idx !== -1) shapeObjectsRef.current.splice(idx, 1);

  const displayLine = new Line(
    [worldPts[0].x, worldPts[0].y, worldPts[1].x, worldPts[1].y],
    {
      stroke: LINE_STROKE,
      strokeWidth: LINE_STROKE_WIDTH,
      selectable: false,
      evented: false,
      objectCaching: false,
    },
  );

  // PO QA 3차: 도면 dot 핸들도 라벨 fontSize 와 같은 비율로 축소 (도면 displayed width 작아질 때 함께 작아짐)
  const dynForHandle = getDynamicLineLabelStyle(canvas);
  const handleRatio = dynForHandle.fontSize / 12;
  const handleRadius = Math.max(3, 6 * handleRatio);
  const handleStroke = Math.max(1, 2 * handleRatio);
  const makeEndpoint = (pt: { x: number; y: number }) =>
    new Circle({
      left: pt.x,
      top: pt.y,
      radius: handleRadius,
      fill: CORNER_COLOR,
      stroke: CORNER_STROKE_COLOR,
      strokeWidth: handleStroke,
      originX: "center",
      originY: "center",
      selectable: true,
      evented: true,
      hasControls: false,
      hasBorders: false,
      hoverCursor: "crosshair",
      moveCursor: "crosshair",
      objectCaching: false,
    });

  const ep0 = makeEndpoint(worldPts[0]);
  const ep1 = makeEndpoint(worldPts[1]);

  setShapeGroup(displayLine, sg);
  setShapeGroup(ep0, sg);
  setShapeGroup(ep1, sg);

  // (F12) 도면에서는 입/퇴장 방향 라벨 제거 — 스냅샷에서만 노출
  const directionObjs: FabricObject[] = [];

  const onEndpointMove = () => {
    displayLine.set({ x1: ep0.left!, y1: ep0.top!, x2: ep1.left!, y2: ep1.top! });
    canvas.requestRenderAll();
  };
  // Cursor 위치로 endpoint 중심을 강제. grab offset 으로 endpoint 가 이미지 경계까지 못 가던 문제 해결 (PO QA 3차 ab-1 의 도면측 이식 — 4차 ag)
  const movingWithCursor = (ep: Circle) => (opt: { e: TPointerEvent }) => {
    const ptr = canvas.getScenePoint(opt.e);
    const bg = getBgImage(canvas);
    const ns = getImageNaturalSize(canvas);
    if (bg && ns) {
      const clamped = clampToImageBounds(ptr, bg, ns);
      ep.set({ left: clamped.x, top: clamped.y });
    }
    onEndpointMove();
  };
  ep0.on("moving", movingWithCursor(ep0));
  ep1.on("moving", movingWithCursor(ep1));
  const upperCanvasEl = () => (canvas as unknown as { upperCanvasEl?: HTMLElement }).upperCanvasEl;
  const setGrabbing = () => {
    const upper = upperCanvasEl();
    if (upper) upper.style.cursor = "crosshair";
  };
  const resetGrabbing = () => {
    const upper = upperCanvasEl();
    if (upper && upper.style.cursor === "crosshair") upper.style.cursor = "";
  };
  ep0.on("mousedown", setGrabbing);
  ep1.on("mousedown", setGrabbing);
  ep0.on("mouseup", resetGrabbing);
  ep1.on("mouseup", resetGrabbing);

  sg.editingMode = "coordinate";
  sg.coordinateParts = { displayLine, ep0, ep1, directionObjs };
  activeCoordinateSgRef.current = sg;

  for (const obj of [displayLine, ep0, ep1, ...directionObjs]) {
    canvas.add(obj);
    shapeObjectsRef.current.push(obj);
  }
  for (const obj of directionObjs) canvas.bringObjectToFront(obj);

  canvas.setActiveObject(ep0);
  canvas.requestRenderAll();
}

export function finishLineCoordinateEdit(
  canvas: Canvas,
  sg: LineShapeGroup,
  shapeObjectsRef: React.MutableRefObject<FabricObject[]>,
  skipRebuildLineIdsRef: React.MutableRefObject<Set<number>>,
  onLineCoordinatesChangeRef: React.MutableRefObject<((lineId: number, coordinates: { x: number; y: number }[]) => void) | undefined>,
  lineLabelDefs: LineLabelDefs,
) {
  const parts = sg.coordinateParts;
  if (!parts) return;

  const { displayLine, ep0, ep1, directionObjs } = parts;
  const worldPts = [{ x: ep0.left!, y: ep0.top! }, { x: ep1.left!, y: ep1.top! }];

  canvas.discardActiveObject();
  for (const obj of [displayLine, ep0, ep1, ...(directionObjs ?? [])]) {
    canvas.remove(obj);
    const i = shapeObjectsRef.current.indexOf(obj);
    if (i !== -1) shapeObjectsRef.current.splice(i, 1);
  }

  rebuildLineGroup(canvas, sg, worldPts, shapeObjectsRef, lineLabelDefs);
  skipRebuildLineIdsRef.current.add(sg.id);

  const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p, canvas)));

  sg.editingMode = "size";
  sg.coordinateParts = null;

  onLineCoordinatesChangeRef.current?.(sg.id, coords);
}

export function enterZoneCoordinateMode(
  canvas: Canvas,
  sg: ZoneShapeGroup,
  shapeObjectsRef: React.MutableRefObject<FabricObject[]>,
  activeCoordinateSgRef: React.MutableRefObject<ShapeGroup | null>,
) {
  const poly = sg.polygon;
  canvas.discardActiveObject();

  const matrix = poly.calcTransformMatrix();
  const poX = poly.pathOffset?.x ?? 0;
  const poY = poly.pathOffset?.y ?? 0;
  const worldPts = poly.points!.map((pt) => {
    const w = util.transformPoint(new Point(pt.x - poX, pt.y - poY), matrix);
    return { x: w.x, y: w.y };
  });
  poly.set({ visible: false, selectable: false, evented: false });
  const nameLabel = shapeObjectsRef.current.find((o) => getShapeGroup(o) === sg && o !== poly);
  if (nameLabel) nameLabel.set({ visible: false });

  const displayFill = new Polyline(
    worldPts.map((p) => ({ x: p.x, y: p.y })),
    { fill: ZONE_FILL, stroke: "transparent", selectable: false, evented: false, objectCaching: false },
  );
  setShapeGroup(displayFill, sg);

  const labelCx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
  const labelCy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
  const displayLabel = createLabelGroup(sg.zoneName ?? "", labelCx, labelCy, { ...ZONE_LABEL_STYLE });
  setShapeGroup(displayLabel, sg);

  const edgeLines: Line[] = [];
  for (let i = 0; i < worldPts.length; i++) {
    const a = worldPts[i];
    const b = worldPts[(i + 1) % worldPts.length];
    const edge = new Line([a.x, a.y, b.x, b.y], {
      stroke: ZONE_STROKE,
      strokeWidth: 2,
      selectable: false,
      evented: false,
      objectCaching: false,
    });
    setShapeGroup(edge, sg);
    edgeLines.push(edge);
  }

  // PO QA 3차: 도면 zone vertex 핸들도 비례 축소
  const dynForVertex = getDynamicLineLabelStyle(canvas);
  const vertexRatio = dynForVertex.fontSize / 12;
  const vertexRadius = Math.max(3, 6 * vertexRatio);
  const vertexStroke = Math.max(1, 2 * vertexRatio);
  const vertexHandles: Circle[] = worldPts.map((pt) =>
    new Circle({
      left: pt.x,
      top: pt.y,
      radius: vertexRadius,
      fill: CORNER_COLOR,
      stroke: CORNER_STROKE_COLOR,
      strokeWidth: vertexStroke,
      originX: "center",
      originY: "center",
      selectable: true,
      evented: true,
      hasControls: false,
      hasBorders: false,
      hoverCursor: "crosshair",
      moveCursor: "crosshair",
      objectCaching: false,
    }),
  );
  for (const vh of vertexHandles) setShapeGroup(vh, sg);

  const onVertexMove = () => {
    const pts = vertexHandles.map((vh) => ({ x: vh.left!, y: vh.top! }));
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % pts.length];
      edgeLines[i].set({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
    displayFill.points = pts.map((p) => ({ x: p.x, y: p.y }));
    // Reset internal state so setDimensions() recalculates from new points
    Object.assign(displayFill, { initialized: false });
    displayFill.setDimensions();
    displayFill.set({ left: displayFill.pathOffset.x, top: displayFill.pathOffset.y });
    displayFill.setCoords();
    const ncx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const ncy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    displayLabel.set({ left: ncx, top: ncy });
    displayLabel.setCoords();
    canvas.requestRenderAll();
  };
  const upperCanvasEl2 = () => (canvas as unknown as { upperCanvasEl?: HTMLElement }).upperCanvasEl;
  const setGrabbingVh = () => {
    const upper = upperCanvasEl2();
    if (upper) upper.style.cursor = "crosshair";
  };
  const resetGrabbingVh = () => {
    const upper = upperCanvasEl2();
    if (upper && upper.style.cursor === "crosshair") upper.style.cursor = "";
  };
  // Cursor 위치로 vertex 중심을 강제 (PO QA 3차 ab-1 의 도면측 이식 — 4차 ag)
  const movingVertexWithCursor = (vh: Circle) => (opt: { e: TPointerEvent }) => {
    const ptr = canvas.getScenePoint(opt.e);
    const bg = getBgImage(canvas);
    const ns = getImageNaturalSize(canvas);
    if (bg && ns) {
      const clamped = clampToImageBounds(ptr, bg, ns);
      vh.set({ left: clamped.x, top: clamped.y });
    }
    onVertexMove();
  };
  for (const vh of vertexHandles) {
    vh.on("moving", movingVertexWithCursor(vh));
    vh.on("mousedown", setGrabbingVh);
    vh.on("mouseup", resetGrabbingVh);
  }

  sg.editingMode = "coordinate";
  sg.coordinateParts = { displayFill, displayLabel, edgeLines, vertexHandles };
  activeCoordinateSgRef.current = sg;

  const allObjs: FabricObject[] = [displayFill, ...edgeLines, ...vertexHandles];
  for (const obj of allObjs) {
    canvas.add(obj);
    shapeObjectsRef.current.push(obj);
  }

  canvas.setActiveObject(vertexHandles[0]);
  canvas.requestRenderAll();
}

export function finishZoneCoordinateEdit(
  canvas: Canvas,
  sg: ZoneShapeGroup,
  shapeObjectsRef: React.MutableRefObject<FabricObject[]>,
  onZoneCoordinatesChangeRef: React.MutableRefObject<((zoneId: number, coordinates: { x: number; y: number }[]) => void) | undefined>,
) {
  const parts = sg.coordinateParts;
  if (!parts) return;

  const { displayFill, displayLabel, edgeLines, vertexHandles } = parts;

  const worldPts = vertexHandles.map((vh) => ({ x: vh.left!, y: vh.top! }));

  canvas.discardActiveObject();
  for (const obj of [displayFill, displayLabel, ...edgeLines, ...vertexHandles]) {
    canvas.remove(obj);
    const i = shapeObjectsRef.current.indexOf(obj);
    if (i !== -1) shapeObjectsRef.current.splice(i, 1);
  }

  const poly = sg.polygon;
  if (poly) {
    const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
    const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
    poly.points = worldPts.map((p) => ({ x: p.x - cx, y: p.y - cy }));
    poly.set({
      originX: "center",
      originY: "center",
      scaleX: 1,
      scaleY: 1,
      angle: 0,
      skewX: 0,
      skewY: 0,
      visible: true,
      stroke: ZONE_STROKE,
      selectable: true,
      evented: true,
      hasControls: true,
      hasBorders: true,
    });
    // Reset internal state so setDimensions() recalculates from new points
    Object.assign(poly, { initialized: false });
    poly.setDimensions();
    poly.set({ left: cx + poly.pathOffset.x, top: cy + poly.pathOffset.y });
    poly.setCoords();
  }

  const nameLabel = shapeObjectsRef.current.find((o) => getShapeGroup(o) === sg && o !== poly);
  if (nameLabel) {
    const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
    const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
    nameLabel.set({ left: cx, top: cy, visible: true });
    nameLabel.setCoords();
  }

  // 편집한 구역은 rebuild 로 다시 그리게 둔다 (skip 하지 않는다).
  // 위 in-place 갱신은 polygon 의 points 만 바꿔 외곽선이 옛 bounding box 로 남는다.
  // skip 까지 걸리면 그 잘못된 그림이 계속 보여 "수정이 원래대로 돌아온다"로 보인다.
  // 좌표는 아래 onZoneCoordinatesChange 로 정확히 반영되므로 그리기는 정상 경로에 맡긴다.

  const coords = worldPts.map((p) => getNormalizedPoint(pixelToPercentStatic(p, canvas)));

  sg.editingMode = "size";
  sg.coordinateParts = null;

  onZoneCoordinatesChangeRef.current?.(sg.id, coords);
}
